<script setup lang="ts">
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { Button, ButtonVariant, useFocusTrap } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { computed, onMounted, onUnmounted } from 'vue';
  import { useModalStore } from '../../store/modal.store';
  import {
    closeModalButtonClass,
    modalBackgroundClass,
    modalContainerClass,
    modalContainerWithCloseClass,
  } from './Modal.css';

  const { t } = useLocale();
  const modalStore = useModalStore();

  const focusTrap = useFocusTrap(
    modalStore.modalState.config?.firstSelectionOnLastItem,
  );

  function keydownListener(event: KeyboardEvent) {
    if (event.key === 'Escape' && !modalStore.modalState.config?.blocking)
      modalStore.closeModal();
  }

  onMounted(() => {
    document.addEventListener('keydown', keydownListener);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', keydownListener);
  });

  const closeOnBackdrop = () => {
    if (!modalStore.modalState.config?.blocking) modalStore.closeModal();
  };

  const showCloseButton = computed<boolean>(() => {
    const config = modalStore.modalState.config;
    return !(config?.blocking && config.hideCloseButton);
  });

  const isVisible = computed<boolean>(() => {
    return !!modalStore.modalState.component;
  });
</script>

<template>
  <Transition name="modal-fade">
    <!-- biome-ignore lint/a11y/noStaticElementInteractions: backdrop, not a control; a role would add a spurious tab stop -->
    <!-- biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard equivalent is the document-level Escape listener above -->
    <div
      v-if="isVisible"
      :class="modalBackgroundClass"
      @click.self="closeOnBackdrop"
    >
      <dialog
        :ref="focusTrap.trapRef"
        :class="[
          modalContainerClass,
          { [modalContainerWithCloseClass]: showCloseButton },
        ]"
        aria-modal="true"
        tabindex="-1"
      >
        <Button
          v-if="showCloseButton"
          :variant="ButtonVariant.Icon"
          is-ghost
          :class="closeModalButtonClass"
          :icon-name="TUNDR_ICONS.CLOSE"
          :aria-label="t('common:actions.close')"
          @click="modalStore.closeModal"
        />
        <component
          :is="modalStore.modalState.component"
          v-bind="modalStore.modalState.props"
        />
      </dialog>
    </div>
  </Transition>
</template>

<style scoped>
  .modal-fade-enter-from,
  .modal-fade-leave-to {
    opacity: 0;
  }

  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: 0.35s ease all;
  }
</style>
